import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import CustomButton from 'lib/designSystem/Button';
import {
  formatMoney,
  formatMoneyBack,
  rutChanger,
  showAlert,
  validaRut,
} from 'lib/functions/utils';
import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

interface Document {
  id: string;
  amount: number;
  paymentDate: string;
  debtorRut: string;
  active: boolean;
}

interface DocumentsFormProps {
  documents: Document[];
  setDocuments: React.Dispatch<React.SetStateAction<Document[]>>;
  operationDate?: string;
}

const DocumentsForm: React.FC<DocumentsFormProps> = ({
  documents,
  setDocuments,
  operationDate,
}) => {
  const dispatch = useDispatch();
  const counterRef = useRef(1);
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
  const [initialDocuments, setInitialDocuments] = useState<Document[]>([]);

  const generateDocumentId = () => {
    const id = `SIM-${String(counterRef.current).padStart(3, '0')}`;
    counterRef.current += 1;
    return id;
  };

  const addDocument = () => {
    const baseDate = operationDate ? new Date(operationDate) : new Date();
    const defaultPaymentDate = new Date(
      baseDate.getTime() + 30 * 24 * 60 * 60 * 1000,
    );

    setDocuments([
      ...documents,
      {
        id: generateDocumentId(),
        amount: 0,
        paymentDate: defaultPaymentDate.toISOString(),
        debtorRut: '',
        active: true,
      },
    ]);
  };

  const removeDocument = (id: string) => {
    if (documents.length > 1) {
      setDocuments(documents.filter((doc) => doc.id !== id));
    }
  };

  const updateDocument = (
    id: string,
    field: 'amount' | 'paymentDate' | 'debtorRut',
    value: string,
  ) => {
    setDocuments(
      documents.map((doc) => {
        if (doc.id === id) {
          if (field === 'amount') {
            const numericValue = formatMoneyBack(value) || 0;
            return { ...doc, amount: numericValue };
          } else if (field === 'paymentDate') {
            const date = new Date(value);
            return { ...doc, paymentDate: date.toISOString() };
          } else if (field === 'debtorRut') {
            return { ...doc, debtorRut: value };
          }
        }
        return doc;
      }),
    );
  };

  const handleOpenDialog = () => {
    // Guardar copia de documentos al abrir
    setInitialDocuments(JSON.parse(JSON.stringify(documents)));
    setOpen(true);
  };

  const handleCancelDialog = () => {
    // Restaurar documentos al estado inicial
    setDocuments(initialDocuments);
    setErrors({});
    setOpen(false);
  };

  const validateAndClose = () => {
    // Validar que todos los documentos tengan monto, fecha y RUT válido
    const newErrors: { [key: string]: boolean } = {};
    let hasErrors = false;

    documents.forEach((doc) => {
      if (doc.amount <= 0) {
        newErrors[`${doc.id}-amount`] = true;
        hasErrors = true;
      }
      if (!doc.paymentDate) {
        newErrors[`${doc.id}-date`] = true;
        hasErrors = true;
      }
      if (!doc.debtorRut || !validaRut(doc.debtorRut)) {
        newErrors[`${doc.id}-rut`] = true;
        hasErrors = true;
      }
    });

    if (hasErrors) {
      setErrors(newErrors);
      showAlert(
        dispatch,
        'error',
        'Por favor completa todos los campos correctamente. Cada documento debe tener un monto mayor a 0, una fecha de vencimiento y un RUT de deudor válido.',
      );
      return;
    }

    // Limpiar errores y cerrar
    setErrors({});
    setOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    validateAndClose();
  };

  React.useEffect(() => {
    const existingNumbers = documents
      .map((doc) => {
        const match = doc.id.match(/^SIM-(\d+)$/);
        return match ? parseInt(match[1], 10) : 0;
      })
      .filter((num) => num > 0);

    if (existingNumbers.length > 0) {
      const maxNumber = Math.max(...existingNumbers);
      counterRef.current = maxNumber + 1;
    }

    if (documents.length === 0) {
      const baseDate = operationDate ? new Date(operationDate) : new Date();
      const defaultPaymentDate = new Date(
        baseDate.getTime() + 30 * 24 * 60 * 60 * 1000,
      );

      setDocuments([
        {
          id: generateDocumentId(),
          amount: 0,
          paymentDate: defaultPaymentDate.toISOString(),
          debtorRut: '',
          active: true,
        },
      ]);
    }
  }, []);

  const totalAmount = documents.reduce((sum, doc) => sum + doc.amount, 0);

  return (
    <>
      <TextField
        value={`${documents.length} documento${documents.length !== 1 ? 's' : ''} - Total: ${formatMoney(totalAmount)}`}
        placeholder="Click para agregar documentos"
        onClick={handleOpenDialog}
        fullWidth
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <DescriptionIcon
                sx={{ color: 'text.secondary' }}
                fontSize="small"
              />
            </InputAdornment>
          ),
          sx: { cursor: 'pointer' },
        }}
      />

      <Dialog open={open} onClose={handleCancelDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            Registro de Documentos
            <IconButton
              onClick={handleCancelDialog}
              size="small"
              sx={{
                color: 'text.secondary',
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box
            component="form"
            sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Header */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1.2fr 1fr 1fr 40px',
                  gap: 1,
                  px: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 500, color: 'text.secondary' }}
                >
                  RUT Deudor
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 500, color: 'text.secondary' }}
                >
                  Monto
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 500, color: 'text.secondary' }}
                >
                  Fecha de Vencimiento
                </Typography>
                <Box />
              </Box>

              {/* Document rows */}
              <Box
                sx={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  pr: 0.5,
                }}
              >
                {documents.map((doc) => (
                  <Box
                    key={doc.id}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1.2fr 1fr 1fr 40px',
                      gap: 1,
                      alignItems: 'start',
                    }}
                  >
                    <TextField
                      placeholder="12.345.678-9"
                      value={doc.debtorRut}
                      onChange={(e) => {
                        rutChanger(e.target.value, (formattedRut) => {
                          updateDocument(doc.id, 'debtorRut', formattedRut);
                          // Limpiar error al cambiar
                          if (errors[`${doc.id}-rut`]) {
                            setErrors({ ...errors, [`${doc.id}-rut`]: false });
                          }
                        });
                      }}
                      size="small"
                      fullWidth
                      error={errors[`${doc.id}-rut`]}
                      helperText={errors[`${doc.id}-rut`] ? 'RUT inválido' : ''}
                    />
                    <TextField
                      placeholder="$0"
                      value={doc.amount > 0 ? formatMoney(doc.amount) : ''}
                      onChange={(e) => {
                        updateDocument(doc.id, 'amount', e.target.value);
                        // Limpiar error al cambiar
                        if (errors[`${doc.id}-amount`]) {
                          setErrors({ ...errors, [`${doc.id}-amount`]: false });
                        }
                      }}
                      size="small"
                      fullWidth
                      error={errors[`${doc.id}-amount`]}
                      helperText={
                        errors[`${doc.id}-amount`] ? 'Monto requerido' : ''
                      }
                    />
                    <TextField
                      type="date"
                      value={doc.paymentDate.split('T')[0]}
                      onChange={(e) => {
                        updateDocument(doc.id, 'paymentDate', e.target.value);
                        // Limpiar error al cambiar
                        if (errors[`${doc.id}-date`]) {
                          setErrors({ ...errors, [`${doc.id}-date`]: false });
                        }
                      }}
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      fullWidth
                      error={errors[`${doc.id}-date`]}
                      helperText={
                        errors[`${doc.id}-date`] ? 'Fecha requerida' : ''
                      }
                    />
                    <IconButton
                      onClick={() => removeDocument(doc.id)}
                      disabled={documents.length === 1}
                      size="small"
                      sx={{ width: 36, height: 36 }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box
              sx={{
                pt: 1,
                borderTop: 1,
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <CustomButton
                color="transparent"
                onClick={addDocument}
                startIcon={<AddIcon fontSize="small" />}
              >
                Agregar Documento
              </CustomButton>
              <CustomButton color="primary" onClick={handleSubmit}>
                Guardar ({documents.length})
              </CustomButton>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DocumentsForm;
